import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { AlertCircle, ClipboardCheck, Loader2, RefreshCw, Search } from 'lucide-react';
import { useSupplierQuestionnaires } from './useSupplierQuestionnaires';
import { SupplierQuestionnaireTable } from './SupplierQuestionnaireTable';
import { HistoryDrawer } from './HistoryDrawer';
import {
  STATUS_LABELS,
  STATUS_ORDER,
  filterRows,
  type DisplayStatus,
  type SupplierQuestionnaireRow,
} from './rows';

type StatusFilter = DisplayStatus | 'all' | 'overdue';

/**
 * CR-5865: the supplier's "My Questionnaires" tab.
 *
 * Every questionnaire assigned to the supplier company — onboarding, ad hoc
 * and recurring, from both assignment systems — in one table with the
 * outstanding action, the score where the customer allows it, and a history
 * drawer per row. Replaces the customer's response-tracking table, which only
 * ever listed one of the two systems for suppliers.
 */
export default function SupplierQuestionnairesPanel() {
  const { rows, loading, error, partial, reload } = useSupplierQuestionnaires();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [historyRow, setHistoryRow] = useState<SupplierQuestionnaireRow | null>(null);

  const visible = useMemo(() => filterRows(rows, search, statusFilter), [rows, search, statusFilter]);
  const outstanding = useMemo(() => rows.filter((r) => r.action !== 'none').length, [rows]);
  const overdue = useMemo(() => rows.filter((r) => r.isOverdue).length, [rows]);

  return (
    <div className="p-6 space-y-4">
      <Card>
        <CardHeader className="py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-primary" />
                My Questionnaires
              </h2>
              <p className="text-sm text-muted-foreground">
                {loading
                  ? 'Loading your questionnaires…'
                  : `${rows.length} questionnaire${rows.length === 1 ? '' : 's'} · ${outstanding} need${outstanding === 1 ? 's' : ''} your action${overdue ? ` · ${overdue} overdue` : ''}`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search questionnaires…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 w-64"
                  aria-label="Search questionnaires"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                <SelectTrigger className="w-48" aria-label="Filter by status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  {STATUS_ORDER.map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="sm" onClick={reload} disabled={loading}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {partial && (
            <div className="flex items-center gap-2 px-6 py-2 text-sm text-amber-800 bg-amber-50 border-y">
              <AlertCircle className="h-4 w-4" /> {partial}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
            </div>
          ) : error ? (
            <div className="flex flex-col items-center py-16 text-center">
              <AlertCircle className="h-8 w-8 text-destructive mb-3" />
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button type="button" variant="outline" onClick={reload}>Try again</Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-center">
              <ClipboardCheck className="h-10 w-10 text-muted-foreground mb-3" />
              <h3 className="text-base font-medium mb-1">No questionnaires yet</h3>
              <p className="text-sm text-muted-foreground">
                No questionnaires have been assigned to you yet. They will appear here when a customer sends one.
              </p>
            </div>
          ) : visible.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No questionnaires match your search or filter.
            </p>
          ) : (
            <SupplierQuestionnaireTable rows={visible} onShowHistory={setHistoryRow} />
          )}
        </CardContent>
      </Card>

      <HistoryDrawer row={historyRow} onClose={() => setHistoryRow(null)} />
    </div>
  );
}
